import {animate, style, transition, trigger} from '@angular/animations';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, isDevMode} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterLinkActive, RouterLink } from '@angular/router';
import {RestMaklerService} from '@app/core/api/makler.service';
import {DeviceService} from '@app/core/services/device.service';
import {FooterVersionSelectService} from '@app/core/services/footer-version-select.service';
import {RouterService} from '@app/core/services/router.service';

import {SidenavType} from '@app/core/sidenav-type';
import {ActiveAssistanceQuery} from '@app/core/state/active-assistance';
import {ActiveBeitragszahlerQuery} from '@app/core/state/active-beitragszahler/active-beitragszahler.query';
import {ActiveBetreuerQuery} from '@app/core/state/active-betreuer/active-betreuer.query';
import {ActiveBetreuerknotenQuery} from '@app/core/state/active-betreuerknoten/active-betreuerknoten.query';
import {ActiveBezugsberechtigterQuery} from '@app/core/state/active-bezugsberechtigter';
import {ActiveFahrzeugQuery} from '@app/core/state/active-fahrzeug';
import {ActiveFamilienpassQuery} from '@app/core/state/active-familienpass';
import {ActiveGebaeudeQuery} from '@app/core/state/active-gebaeude';
import {ActiveHaushaltQuery} from '@app/core/state/active-haushalt';
import {ActiveHotlineQuery} from '@app/core/state/active-hotline';
import {ActiveKundendatenadminQuery} from '@app/core/state/active-kundendatenadmin';
import {ActiveMaklerpersonQuery} from '@app/core/state/active-maklerperson';
import {ActiveMaklerpolizzeQuery} from '@app/core/state/active-maklerpolizze/active-maklerpolizze.query';
import {ActiveMaklerschadenQuery} from '@app/core/state/active-maklerschaden';
import {ActiveMaklervertreterQuery} from '@app/core/state/active-maklervertreter/active-maklervertreter.query';
import {ActivePartnerQuery} from '@app/core/state/active-partner';
import {ActivePersonQuery} from '@app/core/state/active-person';
import {ActivePoliceQuery} from '@app/core/state/active-police';
import {ActivePolizzebuchungQuery} from '@app/core/state/active-polizzebuchung/active-polizzebuchung.query';
import {ActiveSchadenQuery} from '@app/core/state/active-schaden';
import {ActiveSchadenbeteiligterQuery} from '@app/core/state/active-schadenbeteiligter';
import {ActiveSchadenzahlungempfQuery} from '@app/core/state/active-schadenzahlungempf';
import {ActiveTextQuery} from '@app/core/state/active-text';
import {
  ActiveUngeklaerteZahlungQuery
} from '@app/core/state/active-ungeklaerte-zahlung/active-ungeklaerte-zahlung.query';
import {ActiveUserAdminQuery} from '@app/core/state/active-useradmin';
import {ActiveVerbundQuery} from '@app/core/state/active-verbund';
import {ActiveVersichertepersonQuery} from '@app/core/state/active-versicherteperson/active-versicherteperson.query';
import {ActiveVertreterQuery} from '@app/core/state/active-vertreter';
import {ActiveVsbQuery} from '@app/core/state/active-vsb';
import {ActiveZulassungQuery} from '@app/core/state/active-zulassung';
import {SessionQuery} from '@app/core/state/session';
import {UserSettingsQuery, UserSettingsService} from '@app/core/state/user-settings';
import {RightUtil} from '@app/core/util/right.util';
import {GlobalSidenavUtil} from '@app/shared/global-sidenav/global-sidenav.util';
import {GlobalNavCategory, GlobalNavGroup, Navitem, SubItemCategory} from '@app/shell/navitems';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {KeyValue, Schadenbeteiligung} from '@pezi/entities';
import {PartnerBetreuer} from '@pezi/entities/dist/src/main/ts/models/generated/person/partnerbetreuer';
import {NavigationSetting} from '@pezi/entities/dist/src/main/ts/models/generated/user/navigationsetting';
import {Observable, Subscription} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {ActiveMaklerFamilienpassQuery} from "@app/core/state/active-maklerfamilienpass/active-maklerfamilienpass.query";
import { NgIf, NgClass, NgFor, NgStyle } from '@angular/common';
import { TextSmallDirective } from '../../core/directive/text-small.directive';
import { MatIcon } from '@angular/material/icon';
import { MatListItemIcon } from '@angular/material/list';
import { StringUtil } from '../../core/util/string.util';
import { HasRightDirective } from '../directive/hasright.directive';

@UntilDestroy()
@Component({
  selector:        'app-global-sidenav',
  templateUrl:     './global-sidenav.component.html',
  styleUrls:       ['./global-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('parent', [
      transition(':enter', [])
    ]),
    trigger('drawerOpen', [
      transition('void => *', [
        style({
          width:         '0',
          'white-space': 'nowrap'
        }),
        animate('0.2s ease-in-out', style({
          width:         '*',
          'white-space': 'nowrap'
        }))
      ]),
      transition('* => void', [
        style({
          width:         '*',
          'white-space': 'nowrap'
        }),
        animate('0.2s ease-in-out', style({
          width:         '0',
          'white-space': 'nowrap'
        }))
      ])
    ]),
    trigger('expandCollapse', [
      transition('void => *', [
        style({
          height: '0'
        }),
        animate('0.2s ease-in-out', style({
          height: '*'
        }))
      ]),
      transition('* => void', [
        style({
          height: '*'
        }),
        animate('0.2s ease-in-out', style({
          height: '0'
        }))
      ])
    ])
  ],
  standalone: true,
  imports:    [
    NgIf,
    NgClass,
    TextSmallDirective,
    MatIcon,
    NgFor,
    RouterLinkActive,
    RouterLink,
    NgStyle,
    MatListItemIcon,
    HasRightDirective
  ]
})
export class GlobalSidenavComponent {

  @Input()
  set globalDrawerItems(value: GlobalNavCategory[]) {
    if (value) {
      this.initFilteredAndGroupedNavCategories(value);
    }
    this._globalDrawerItems = value;
  }

  get globalDrawerItems(): GlobalNavCategory[] {
    return this._globalDrawerItems;
  }

  @Input()
  openFromRight = false;

  @Input()
  compact = false;

  @Input()
  mobileOnly = false;

  // Mobiles absolute-position Sidenav muss Platz für Update-Overlay machen
  @Input()
  updateOverlayShown = false;

  isDrawerOpen = false;

  public hightlightedNavItems: NavigationSetting[] = [];
  public drawerExpanded = false;
  public subItems: SubItemCategory[];
  public currentBetreuer: PartnerBetreuer;

  public currentActiveSidenavType: SidenavType;

  public groupedAndFilteredDrawerItems: KeyValue<GlobalNavGroup, GlobalNavCategory[]>[] = [];
  public NO_GROUP = 'none';
  private _globalDrawerItems: GlobalNavCategory[];

  private currentPolSub: Subscription;
  private currentVertragSub: Subscription;
  private currentPositionSub: Subscription;
  private currentPersonSub: Subscription;
  private currentSchadenSub: Subscription;
  private currentFahrzeugSub: Subscription;
  private currentVertreterSub: Subscription;
  private currentHaushaltSub: Subscription;
  private currentGebaeudeSub: Subscription;
  private currentHotlineSub: Subscription;
  private currentVsbSub: Subscription;
  private currentBetreuerSub: Subscription;
  private currentVerbundSub: Subscription;
  private currentAssistanceSub: Subscription;
  private currentBetreuerknotenSub: Subscription;
  private currentTextSub: Subscription;
  private currentFamilienpassSub: Subscription;
  private currentPartnerSub: Subscription;
  private currentSchadenzahlungempfSub: Subscription;
  private currentSchadenbeteiligterSub: Subscription;
  private currentZulassungSub: Subscription;
  private currentBezugsberechtigterSub: Subscription;
  private currentBenutzerSub: Subscription;
  private currentMaklerpersonSub: Subscription;
  private currentMaklerpolizzeSub: Subscription;
  private currentMaklerschadenSub: Subscription;
  private currentBeitragszahlerSub: Subscription;
  private currentVersichertePersonSub: Subscription;
  private currentPolizzebuchungSub: Subscription;
  private currentMaklervertreterSub: Subscription;
  private currentMaklerBTRSub: Subscription;
  private currentKundenDatenAdminSub: Subscription;
  private currentUngeklaerteZahlungenSub: Subscription;
  private currentMaklerFamlienpassSub: Subscription;

  private currentSubPage: ActivatedRoute;

  constructor(public deviceService: DeviceService, private cd: ChangeDetectorRef, private route: ActivatedRoute,
              private sessionQuery: SessionQuery, public routerService: RouterService,
              private router: Router, private activePoliceQuery: ActivePoliceQuery,
              private activePersonQuery: ActivePersonQuery, private activeSchadenQuery: ActiveSchadenQuery, private activeFahrzeugQuery: ActiveFahrzeugQuery,
              private activeVertreterQuery: ActiveVertreterQuery, private activeHaushaltQuery: ActiveHaushaltQuery,
              private activeGebaeudeQuery: ActiveGebaeudeQuery, private activeHotlineQuery: ActiveHotlineQuery,
              private activeVsbQuery: ActiveVsbQuery, private activeBetreuerQuery: ActiveBetreuerQuery, private activeVerbundQuery: ActiveVerbundQuery,
              private activeAssistanceQuery: ActiveAssistanceQuery, private activeBetreuerknotenQuery: ActiveBetreuerknotenQuery,
              private activeTextQuery: ActiveTextQuery, private activeFamilienpassQuery: ActiveFamilienpassQuery,
              private activePartnerQuery: ActivePartnerQuery, private activeSchadenzahlungempfQuery: ActiveSchadenzahlungempfQuery,
              private activeSchadenbeteiligterQuery: ActiveSchadenbeteiligterQuery, private activeZulassungQuery: ActiveZulassungQuery,
              private activeBezugsberechtigterQuery: ActiveBezugsberechtigterQuery, private activeUserAdminQuery: ActiveUserAdminQuery,
              private activeMaklerpersonQuery: ActiveMaklerpersonQuery, private activeMaklerpolizzeQuery: ActiveMaklerpolizzeQuery,
              private activeMaklerschadenQuery: ActiveMaklerschadenQuery, private activeBeitragszahlerQuery: ActiveBeitragszahlerQuery,
              private activeVersichertepersonQuery: ActiveVersichertepersonQuery, private activePolizzebuchungQuery: ActivePolizzebuchungQuery,
              private activeMaklervertreterQuery: ActiveMaklervertreterQuery, private activeUngeklaerteZahlungQuery: ActiveUngeklaerteZahlungQuery,
              private activeKundendatenadminQuery: ActiveKundendatenadminQuery, private activeMaklerFamilienpassQuery: ActiveMaklerFamilienpassQuery,
              private rightUtil: RightUtil, private maklerService: RestMaklerService, private snackBar: MatSnackBar, private settingsQuery: UserSettingsQuery,
              private footerVersionSelectService: FooterVersionSelectService, private settingService: UserSettingsService) {
    this.deviceService
      .getDeviceSubject()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (!deviceService.isMobile()) {
          this.isDrawerOpen = false;
        }
        this.cd.markForCheck();
      });
    this.sessionQuery.currentUser$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(usr => {
        if (usr && usr.betreuer && usr.betreuerAuswahlList && usr.betreuerAuswahlList.length > 0) {
          const betr = usr.betreuerAuswahlList.find(b => b.personId === usr.betreuer.perS);
          this.currentBetreuer = betr;

        }
        if (this._globalDrawerItems) { // Berechtigungen könnten sich geändert haben
          this.initFilteredAndGroupedNavCategories(this._globalDrawerItems);
        }
        this.cd.markForCheck();
      });
    this.settingsQuery.currentSettings$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(store => {
        if (store) {
          this.hightlightedNavItems = store.navigation;
          this.cd.markForCheck();
        }
      });

    const onNavigationEnd = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

    // Change page title on navigation
    onNavigationEnd
      .pipe(
        map(() => {
          let route0 = this.route;
          while (route0.firstChild) {
            route0 = route0.firstChild;
          }
          return route0;
        }),
        filter(route1 => route1.outlet === 'primary'),
        switchMap(route2 => route2.data, (activatedRoute, data) => ({
          activatedRoute,
          data
        })),
        untilDestroyed(this)
      )
      .subscribe((res) => {
        const data = res.data;
        this.currentSubPage = res.activatedRoute as ActivatedRoute;
        this.resetAllSubs();
        this.footerVersionSelectService.currentVersions.next(null);
        if (data.sidenav) {
          if (data.sidenav !== this.currentActiveSidenavType) {
            this.subItems = null;
          }
          if (data.sidenav === SidenavType.POLIZZE) {
            this.currentPolSub = this.initRouteBuilderSub(SidenavType.POLIZZE, this.activePoliceQuery.currentPolice$);
          }
          if (data.sidenav === SidenavType.VERTRAG) {
            this.currentVertragSub = this.initRouteBuilderSub(SidenavType.VERTRAG, this.activePoliceQuery.currentVertrag$);
          }
          if (data.sidenav === SidenavType.POSITION) {
            this.currentPositionSub = this.initRouteBuilderSub(SidenavType.POSITION, this.activePoliceQuery.currentPosition$);
          }
          if (data.sidenav === SidenavType.PERSON) {
            this.currentPersonSub = this.initRouteBuilderSub(SidenavType.PERSON, this.activePersonQuery.currentPerson$);
          }
          if (data.sidenav === SidenavType.FAHRZEUG) {
            this.currentFahrzeugSub = this.initRouteBuilderSub(SidenavType.FAHRZEUG, this.activeFahrzeugQuery.currentFahrzeug$);
          }
          if (data.sidenav === SidenavType.SCHADEN) {
            this.currentSchadenSub = this.initRouteBuilderSub(SidenavType.SCHADEN, this.activeSchadenQuery.currentSchaden$);
          }
          if (data.sidenav === SidenavType.VERTRETER) {
            this.currentVertreterSub = this.initRouteBuilderSub(SidenavType.VERTRETER, this.activeVertreterQuery.currentVertreter$);
          }
          if (data.sidenav === SidenavType.HAUSHALT) {
            this.currentHaushaltSub = this.initRouteBuilderSub(SidenavType.HAUSHALT, this.activeHaushaltQuery.currentHaushalt$);
          }
          if (data.sidenav === SidenavType.GEBAEUDE) {
            this.currentGebaeudeSub = this.initRouteBuilderSub(SidenavType.GEBAEUDE, this.activeGebaeudeQuery.currentGebaeude$);
          }
          if (data.sidenav === SidenavType.HOTLINE) {
            this.currentHotlineSub = this.initRouteBuilderSub(SidenavType.HOTLINE, this.activeHotlineQuery.currentPerson$);
          }
          if (data.sidenav === SidenavType.VSB) {
            this.currentVsbSub = this.initRouteBuilderSub(SidenavType.VSB, this.activeVsbQuery.currentVsb$);
          }
          if (data.sidenav === SidenavType.BETREUER) {
            this.currentBetreuerSub = this.initRouteBuilderSub(SidenavType.BETREUER, this.activeBetreuerQuery.currentBetreuer$);
          }
          if (data.sidenav === SidenavType.VERBUND) {
            this.currentVerbundSub = this.initRouteBuilderSub(SidenavType.VERBUND, this.activeVerbundQuery.currentVerbund$);
          }
          if (data.sidenav === SidenavType.ASSISTANCE) {
            this.currentAssistanceSub = this.initRouteBuilderSub(SidenavType.ASSISTANCE, this.activeAssistanceQuery.currentPerson$);
          }
          if (data.sidenav === SidenavType.ASSISTANCEPOLIZZE) {
            this.currentAssistanceSub = this.initRouteBuilderSub(SidenavType.ASSISTANCEPOLIZZE, this.activeAssistanceQuery.currentPolizze$);
          }
          if (data.sidenav === SidenavType.BETREUERKNOTEN) {
            this.currentBetreuerknotenSub = this.initRouteBuilderSub(SidenavType.BETREUERKNOTEN, this.activeBetreuerknotenQuery.currentBetreuerknoten$);
          }
          if (data.sidenav === SidenavType.TEXTE) {
            this.currentTextSub = this.initRouteBuilderSub(SidenavType.TEXTE, this.activeTextQuery.currentText$);
          }
          if (data.sidenav === SidenavType.ZAHLUNG) {
            this.currentPolSub = this.initRouteBuilderSub(SidenavType.ZAHLUNG, this.activePoliceQuery.currentPolice$, res.activatedRoute.snapshot.params);
          }
          if (data.sidenav === SidenavType.POLIZZEBUCHUNG) {
            this.currentPolizzebuchungSub =
              this.initRouteBuilderSub(SidenavType.POLIZZEBUCHUNG, this.activePolizzebuchungQuery.currentPolizzebuchung$, this.getAllParamsFromActivatedRoute(res.activatedRoute));
          }
          if (data.sidenav === SidenavType.FAMILIENPASS) {
            this.currentFamilienpassSub = this.initRouteBuilderSub(SidenavType.FAMILIENPASS, this.activeFamilienpassQuery.currentFamilienpass$);
          }
          if (data.sidenav === SidenavType.PARTNER) {
            this.currentPartnerSub = this.initRouteBuilderSub(SidenavType.PARTNER, this.activePartnerQuery.currentPartner$);
          }
          if (data.sidenav === SidenavType.SCHADENZAHLUNGEMPF) {
            this.currentSchadenzahlungempfSub = this.initRouteBuilderSub(SidenavType.SCHADENZAHLUNGEMPF, this.activeSchadenzahlungempfQuery.currentSchadenzahlungempf$);
          }
          if (data.sidenav === SidenavType.SCHADENBETEILIGTER) {
            this.currentSchadenbeteiligterSub = this.initRouteBuilderSub(SidenavType.SCHADENBETEILIGTER, this.activeSchadenbeteiligterQuery.currentSchadenbeteiligter$);
          }
          if (data.sidenav === SidenavType.ZULASSUNG) {
            this.currentZulassungSub = this.initRouteBuilderSub(SidenavType.ZULASSUNG, this.activeZulassungQuery.currentZulassung$);
          }
          if (data.sidenav === SidenavType.BEZUGSBERECHTIGTER) {
            this.currentBezugsberechtigterSub = this.initRouteBuilderSub(SidenavType.BEZUGSBERECHTIGTER, this.activeBezugsberechtigterQuery.currentBezugsberechtigter$);
          }
          if (data.sidenav === SidenavType.BENUTZER) {
            this.currentBenutzerSub = this.initRouteBuilderSub(SidenavType.BENUTZER, this.activeUserAdminQuery.currentUserAdmin$);
          }
          if (data.sidenav === SidenavType.MAKLERPERSON) {
            this.currentMaklerpersonSub = this.initRouteBuilderSub(SidenavType.MAKLERPERSON, this.activeMaklerpersonQuery.currentMaklerPerson$);
          }
          if (data.sidenav === SidenavType.MAKLERPOLIZZE) {
            this.currentMaklerpolizzeSub = this.initRouteBuilderSub(SidenavType.MAKLERPOLIZZE, this.activeMaklerpolizzeQuery.currentMaklerPolizze$);
          }
          if (data.sidenav === SidenavType.MAKLERSCHADEN) {
            this.currentMaklerschadenSub = this.initRouteBuilderSub(SidenavType.MAKLERSCHADEN, this.activeMaklerschadenQuery.currentMaklerSchaden$);
          }
          if (data.sidenav === SidenavType.BEITRAGSZAHLER) {
            this.currentMaklerBTRSub = this.initRouteBuilderSub(SidenavType.BEITRAGSZAHLER, this.activeBeitragszahlerQuery.currentBeitragszahler$);
          }
          if (data.sidenav === SidenavType.VERSPERSON) {
            this.currentVersichertePersonSub = this.initRouteBuilderSub(SidenavType.VERSPERSON, this.activeVersichertepersonQuery.currentVersicherteperson$);
          }
          if (data.sidenav === SidenavType.MAKLERVERTRETER) {
            this.currentMaklervertreterSub = this.initRouteBuilderSub(SidenavType.MAKLERVERTRETER, this.activeMaklervertreterQuery.currentMaklerVertreter$);
          }
          if (data.sidenav === SidenavType.MAKLERDOWNLOAD) {
            // No Subscription needed here
            this.currentActiveSidenavType = SidenavType.MAKLERDOWNLOAD;
            this.subItems = this.getSideNavFromTypeAndInitFooterVersions(this.currentActiveSidenavType, null, null);
            this.drawerExpanded = true;
            this.cd.markForCheck();
          }
          if (data.sidenav === SidenavType.CMS) {
            // No Subscription needed here
            this.currentActiveSidenavType = SidenavType.CMS;
            this.subItems = this.getSideNavFromTypeAndInitFooterVersions(this.currentActiveSidenavType, null, null);
            this.drawerExpanded = true;
            this.cd.markForCheck();
          }
          if (data.sidenav === SidenavType.ZULASSUNG_KENNZEICHEN) {
            // No Subscription needed here
            this.currentActiveSidenavType = SidenavType.ZULASSUNG_KENNZEICHEN;
            this.subItems = this.getSideNavFromTypeAndInitFooterVersions(this.currentActiveSidenavType, null, this.getAllParamsFromActivatedRoute(res.activatedRoute));
            this.drawerExpanded = true;
            this.cd.markForCheck();
          }
          if (data.sidenav === SidenavType.DATENSCHUTZ) {
            // No Subscription needed here
            this.currentActiveSidenavType = SidenavType.DATENSCHUTZ;
            this.subItems = this.getSideNavFromTypeAndInitFooterVersions(this.currentActiveSidenavType, null, null);
            this.drawerExpanded = true;
            this.cd.markForCheck();
          }
          if (data.sidenav === SidenavType.KUNDENDATENADMIN) {
            this.currentKundenDatenAdminSub = this.initRouteBuilderSub(SidenavType.KUNDENDATENADMIN, this.activeKundendatenadminQuery.currentKundendatenadmin$);
          }
          if (data.sidenav === SidenavType.UNGEKLAERTEZAHLUNGEN_ARCHIV) {
            this.currentUngeklaerteZahlungenSub = this.initRouteBuilderSub(SidenavType.UNGEKLAERTEZAHLUNGEN_ARCHIV, this.activeUngeklaerteZahlungQuery.currentUngeklaerteZahlung$);
          }
          if (data.sidenav === SidenavType.MAKLERFAMILIENPASS) {
            this.currentMaklerFamlienpassSub = this.initRouteBuilderSub(SidenavType.MAKLERFAMILIENPASS, this.activeMaklerFamilienpassQuery.currentFamilienpass$);
          }
          if (data.sidenav === SidenavType.BETREUERDATENARCHIVADMIN) {
            // No Subscription needed here
            this.currentActiveSidenavType = SidenavType.BETREUERDATENARCHIVADMIN;
            this.subItems = this.getSideNavFromTypeAndInitFooterVersions(this.currentActiveSidenavType, null, null);
            this.drawerExpanded = true;
            this.cd.markForCheck();
          }
          if (data.sidenav === SidenavType.REFERATADMIN) {
            // No Subscription needed here
            this.currentActiveSidenavType = SidenavType.REFERATADMIN;
            this.subItems = this.getSideNavFromTypeAndInitFooterVersions(this.currentActiveSidenavType, null, null);
            this.drawerExpanded = true;
            this.cd.markForCheck();
          }
        } else {
          this.currentActiveSidenavType = null;
          this.subItems = null;
        }
        this.cd.markForCheck();
      });
  }

  public toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
    this.cd.markForCheck();
  }

  public isCategorySidenavVisible(category: GlobalNavCategory): boolean {
    if (category.possibleChildSideNavs) {
      return category.possibleChildSideNavs.findIndex(sn => sn === this.currentActiveSidenavType) !== -1;
    }
    return false;
  }

  public isDevMode(): boolean {
    return isDevMode();
  }

  public getFavoriteCategoryColor(category: GlobalNavCategory): string {
    if (this.hightlightedNavItems) {
      const idx = this.hightlightedNavItems.findIndex(usrNavItem => usrNavItem.id === category.id);
      if (idx !== -1) {
        return this.hightlightedNavItems[idx].color ? this.hightlightedNavItems[idx].color : '#33649d';
      }
    }
    return null;
  }

  public handleGlobalItemClick(item: GlobalNavCategory): void {
    this.isDrawerOpen = false;
    this.drawerExpanded = false;
  }

  public handleItemClick(item: Navitem): void {
    this.isDrawerOpen = false;
    this.drawerExpanded = true;
    this.router.navigate([item.dynamicURLPart ? item.dynamicURLPart + item.routerUrl : item.routerUrl], {
      relativeTo:  this.route,
      queryParams: item.queryParams
    });
  }

  public handleHeaderItemClick(item: Navitem): void {
    this.isDrawerOpen = false;
    this.drawerExpanded = true;
    this.router.navigate([item.dynamicURLPart ? item.dynamicURLPart + item.routerUrl : item.routerUrl], {
      relativeTo:  this.route,
      queryParams: item.queryParams
    });
  }

  public collapseExpand(group: GlobalNavGroup): void {
    group.expanded = !group.expanded;
  }

  private initRouteBuilderSub(sidenavType: SidenavType, obs$: Observable<any>, params?: Params): Subscription {
    this.currentActiveSidenavType = sidenavType;
    return obs$.pipe(untilDestroyed(this))
      .subscribe(v => {
        if (v) {
          this.subItems = this.getSideNavFromTypeAndInitFooterVersions(sidenavType, v, params);
          this.drawerExpanded = true;
        }
        this.cd.markForCheck();
      });
  }

  /**
   * Used if all Params of the url tree are needed, since Activatedroute may not contain the full route,
   * if the current page is part of a child route
   * @param route
   * @private
   */
  private getAllParamsFromActivatedRoute(route: ActivatedRoute): Params {
    const params: Params = route.snapshot.params ? JSON.parse(JSON.stringify(route.snapshot.params)) : {};
    if (route.pathFromRoot && route.pathFromRoot.length > 0) {
      route.pathFromRoot.forEach(routePart => {
        if (routePart.snapshot.params) {
          Object.keys(routePart.snapshot.params)
            .forEach(key => {
              if (!params[key]) {
                params[key] = routePart.snapshot.params[key];
              }
            });
        }
      });
    }
    return params;
  }

  private initFilteredAndGroupedNavCategories(navCats: GlobalNavCategory[]): void {
    this.groupedAndFilteredDrawerItems = [
      {
        key:   {
          id:       this.NO_GROUP,
          header:   '',
          expanded: true
        },
        value: []
      }
    ];
    navCats.forEach(item => {
      if (item.group) {
        if (this.checkIfCategoryItemVisible(item)) {
          // only add group if at least one member visible
          if (this.groupedAndFilteredDrawerItems.findIndex(gr => gr.key.id === item.group.id) === -1) {
            this.groupedAndFilteredDrawerItems.push({
              key:   item.group,
              value: []
            });
          }
          this.groupedAndFilteredDrawerItems.find(e => e.key.id === item.group.id)
            .value
            .push(item);
        }
      } else {
        if (this.checkIfCategoryItemVisible(item)) {
          this.groupedAndFilteredDrawerItems.find(e => e.key.id === this.NO_GROUP)
            .value
            .push(item);
        }
      }
    });

    this.groupedAndFilteredDrawerItems.sort((a, b) => {
      if (!a.key.order || !b.key.order) {
        return 0;
      }
      if (a.key.order < b.key.order) {
        return -1;
      }
      if (a.key.order > b.key.order) {
        return 1;
      }
      return 0;
    });

  }

  private checkIfCategoryItemVisible(cat: GlobalNavCategory): boolean {
    if (cat.right) {
      if (Array.isArray(cat.right)) {
        if (cat.rightsLinkedAsOR) {
          if (this.rightUtil.hasAnyOfGivenRights(cat.right)) {
            return !cat.devOnly || isDevMode();
          }
        } else if (this.rightUtil.hasAllOfGivenRights(cat.right)) {
          return !cat.devOnly || isDevMode();
        }
      }
      if (this.rightUtil.hasRight(cat.right)) {
        return !cat.devOnly || isDevMode();
      }
      return false;
    }
    return !cat.devOnly || isDevMode();
  }

  private getSideNavFromTypeAndInitFooterVersions(sidenavType: SidenavType, entity: any, params?: Params): SubItemCategory[] {
    const subPage = this.currentSubPage && this.currentSubPage.snapshot && this.currentSubPage.snapshot.url && this.currentSubPage.snapshot.url.length > 0 ? this.currentSubPage.snapshot.url[0].path : null;
    switch (sidenavType) {
      case SidenavType.POLIZZE:
        this.footerVersionSelectService.initNewVersions(entity.id, SidenavType.POLIZZE, entity.versionen, subPage);
        return GlobalSidenavUtil.getPoliceSidenav(entity);
      case SidenavType.PERSON:
        let vers = null;
        if (entity.versionen && entity.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.version) {
            vers = entity.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(vers, SidenavType.PERSON, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getPersonSideNav(entity, vers ? vers.toString() : null);
      case SidenavType.VERTRAG:
        // this.footerVersionSelectService.initNewVersions(null, SidenavType.VERTRAG, entity.versionen, subPage)
        return GlobalSidenavUtil.getVertragSideNav(entity);
      case SidenavType.POSITION:
        //this.footerVersionSelectService.initNewVersions(null, SidenavType.POSITION, entity.versionen, subPage)
        return GlobalSidenavUtil.getPositionSideNav(entity);
      case SidenavType.VERTRETER:
        let vtrvers = null;
        if (entity.versionen && entity.person.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.person.version) {
            vtrvers = entity.person.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(vtrvers, SidenavType.VERTRETER, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getVertreterSideNav(entity, vtrvers ? vtrvers.toString() : null);
      case SidenavType.FAHRZEUG:
        let fzvers = null;
        if (entity.versionen && entity.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.version) {
            fzvers = entity.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(fzvers, SidenavType.FAHRZEUG, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getFahrzeugSideNav(entity.id.toString(), fzvers);
      case SidenavType.SCHADEN:
        this.footerVersionSelectService.initNewVersions(entity.id, SidenavType.SCHADEN, entity.versionen, subPage);
        return GlobalSidenavUtil.getSchadenSideNav(entity);
      case SidenavType.HAUSHALT:
        return GlobalSidenavUtil.getHaushaltSideNav(entity.adresse.adresseId.toString());
      case SidenavType.GEBAEUDE:
        let gebvers = null;
        if (entity.versionen && entity.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.version) {
            gebvers = entity.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(gebvers, SidenavType.GEBAEUDE, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getGebaeudeSideNav(entity.id, gebvers);
      case SidenavType.HOTLINE:
        return GlobalSidenavUtil.getHotlineSideNav(entity.personResult.id);
      case SidenavType.VSB:
        return GlobalSidenavUtil.getVsbSideNav(entity.id);
      case SidenavType.BETREUER:
        let btrvers = null;
        if (entity.versionen && entity.person.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.person.version) {
            btrvers = entity.person.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(btrvers, SidenavType.BETREUER, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getBetreuerSidenav(entity, btrvers ? btrvers.toString() : null);
      case SidenavType.VERBUND:
        return GlobalSidenavUtil.getVerbundSideNav(entity.person.id);
      case SidenavType.ASSISTANCE:
        return GlobalSidenavUtil.getAssistanceSideNav(entity.personResult.id);
      case SidenavType.ASSISTANCEPOLIZZE:
        return GlobalSidenavUtil.getAssistancePolizzeSideNav(entity.persId, entity.polizze.id);
      case SidenavType.BETREUERKNOTEN:
        return GlobalSidenavUtil.getBetreuerknotenSidenav(entity);
      case SidenavType.TEXTE:
        return GlobalSidenavUtil.getTexteSidenav(entity);
      case SidenavType.ZAHLUNG:
        return GlobalSidenavUtil.getZahlungSidenav(entity.id, params.zahlungid);
      case SidenavType.POLIZZEBUCHUNG:
        return GlobalSidenavUtil.getPolizzebuchungSidenav(entity, params.polint);
      case SidenavType.FAMILIENPASS:
        return GlobalSidenavUtil.getFamilienpassSidenav(entity);
      case SidenavType.MAKLERFAMILIENPASS:
        return GlobalSidenavUtil.getMaklerFamilienpassSidenav(entity);
      case SidenavType.PARTNER:
        let partnerVers = null;
        if (entity.versionen && entity.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.version) {
            partnerVers = entity.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(partnerVers, SidenavType.PARTNER, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getPartnerSidenav(entity, partnerVers);
      case SidenavType.SCHADENZAHLUNGEMPF:
        return GlobalSidenavUtil.getSchadenzahlungempfSidenav(entity);
      case SidenavType.SCHADENBETEILIGTER:
        return GlobalSidenavUtil.getSchadenbeteiligterSidenav(
          StringUtil.getSchadenbeteiligterNavigationStr(
            (entity as Schadenbeteiligung).personId, (entity as Schadenbeteiligung).kfzKz, (entity as Schadenbeteiligung).fremndAnstaltNr, (entity as Schadenbeteiligung).fremndAnstaltPolNr));
      case SidenavType.ZULASSUNG:
        return GlobalSidenavUtil.getZulassungSidenav(entity);
      case SidenavType.BEZUGSBERECHTIGTER:
        let bzvers = null;
        if (entity.versionen && entity.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.version) {
            bzvers = entity.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(bzvers, SidenavType.BEZUGSBERECHTIGTER, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getBezugsberechtigterSidenav(entity.id.toString(), bzvers);
      case SidenavType.BENUTZER:
        return GlobalSidenavUtil.getUserAdminSideNav(entity.userid.toString());
      case SidenavType.MAKLERPERSON:
        return GlobalSidenavUtil.getMaklerpersonSidenav(entity);
      case SidenavType.MAKLERPOLIZZE:
        return GlobalSidenavUtil.getMaklerpolizzeSidenav(entity);
      case SidenavType.MAKLERSCHADEN:
        return GlobalSidenavUtil.getMaklerschadenSidenav(entity);
      case SidenavType.MAKLERVERTRETER:
        return GlobalSidenavUtil.getMaklervertreterSidenav(entity);
      case SidenavType.BEITRAGSZAHLER:
        let beitragszahlerVers = null;
        if (entity.versionen && entity.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.version) {
            beitragszahlerVers = entity.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(beitragszahlerVers, SidenavType.BEITRAGSZAHLER, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getBeitragszahlerSidenav(entity, beitragszahlerVers);
      case SidenavType.VERSPERSON:
        let verspersonVers = null;
        if (entity.versionen && entity.version && entity.versionen.length > 0) {
          if (entity.versionen[0].version !== entity.version) {
            verspersonVers = entity.version; // Only use not most recent version for path
          }
          this.footerVersionSelectService.initNewVersions(verspersonVers, SidenavType.VERSPERSON, entity.versionen, subPage);
        }
        return GlobalSidenavUtil.getVersichertepersonsidenav(entity, verspersonVers);
      case SidenavType.MAKLERDOWNLOAD:
        return GlobalSidenavUtil.getMaklerDLSidenav();
      case SidenavType.CMS:
        return GlobalSidenavUtil.getCMSSidenav();
      case SidenavType.ZULASSUNG_KENNZEICHEN:
        return GlobalSidenavUtil.getZulassungKennzeichenSidenav(params.kennzeichen);
      case SidenavType.UNGEKLAERTEZAHLUNGEN_ARCHIV:
        return GlobalSidenavUtil.getUngeklaerteZahlungenArchivSidenav(entity);
      case SidenavType.KUNDENDATENADMIN:
        return GlobalSidenavUtil.getKundenAdminSideNav(entity.person.id.toString());
      case SidenavType.BETREUERDATENARCHIVADMIN:
        return GlobalSidenavUtil.getBetreuerdatenAdminSidenav();
      case SidenavType.REFERATADMIN:
        return GlobalSidenavUtil.getReferatAdminSidenav();
    }
    return [];
  }

  private resetAllSubs(): void {
    if (this.currentPolSub) {
      this.currentPolSub.unsubscribe();
    }
    if (this.currentVertragSub) {
      this.currentVertragSub.unsubscribe();
    }
    if (this.currentPositionSub) {
      this.currentPositionSub.unsubscribe();
    }
    if (this.currentPersonSub) {
      this.currentPersonSub.unsubscribe();
    }
    if (this.currentSchadenSub) {
      this.currentSchadenSub.unsubscribe();
    }
    if (this.currentFahrzeugSub) {
      this.currentFahrzeugSub.unsubscribe();
    }
    if (this.currentVertreterSub) {
      this.currentVertreterSub.unsubscribe();
    }
    if (this.currentHaushaltSub) {
      this.currentHaushaltSub.unsubscribe();
    }
    if (this.currentGebaeudeSub) {
      this.currentGebaeudeSub.unsubscribe();
    }
    if (this.currentHotlineSub) {
      this.currentHotlineSub.unsubscribe();
    }
    if (this.currentVsbSub) {
      this.currentVsbSub.unsubscribe();
    }
    if (this.currentBetreuerSub) {
      this.currentBetreuerSub.unsubscribe();
    }
    if (this.currentVerbundSub) {
      this.currentVerbundSub.unsubscribe();
    }
    if (this.currentAssistanceSub) {
      this.currentAssistanceSub.unsubscribe();
    }
    if (this.currentBetreuerknotenSub) {
      this.currentBetreuerknotenSub.unsubscribe();
    }
    if (this.currentTextSub) {
      this.currentTextSub.unsubscribe();
    }
    if (this.currentFamilienpassSub) {
      this.currentFamilienpassSub.unsubscribe();
    }
    if (this.currentMaklerFamlienpassSub) {
      this.currentMaklerFamlienpassSub.unsubscribe();
    }
    if (this.currentPartnerSub) {
      this.currentPartnerSub.unsubscribe();
    }
    if (this.currentSchadenzahlungempfSub) {
      this.currentSchadenzahlungempfSub.unsubscribe();
    }
    if (this.currentSchadenbeteiligterSub) {
      this.currentSchadenbeteiligterSub.unsubscribe();
    }
    if (this.currentZulassungSub) {
      this.currentZulassungSub.unsubscribe();
    }
    if (this.currentBezugsberechtigterSub) {
      this.currentBezugsberechtigterSub.unsubscribe();
    }
    if (this.currentBenutzerSub) {
      this.currentBenutzerSub.unsubscribe();
    }
    if (this.currentMaklerpersonSub) {
      this.currentMaklerpersonSub.unsubscribe();
    }
    if (this.currentMaklerpolizzeSub) {
      this.currentMaklerpolizzeSub.unsubscribe();
    }
    if (this.currentMaklerschadenSub) {
      this.currentMaklerschadenSub.unsubscribe();
    }
    if (this.currentBeitragszahlerSub) {
      this.currentBeitragszahlerSub.unsubscribe();
    }
    if (this.currentVersichertePersonSub) {
      this.currentVersichertePersonSub.unsubscribe();
    }
    if (this.currentPolizzebuchungSub) {
      this.currentPolizzebuchungSub.unsubscribe();
    }
    if (this.currentMaklervertreterSub) {
      this.currentMaklervertreterSub.unsubscribe();
    }
    if (this.currentMaklerBTRSub) {
      this.currentMaklerBTRSub.unsubscribe();
    }
    if (this.currentUngeklaerteZahlungenSub) {
      this.currentUngeklaerteZahlungenSub.unsubscribe();
    }
  }
}
